"use client";

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";

import type { CreatorProfileForMyLink } from "@artha/types";
import type {
  CandidatePreferenceSeedDto,
  JobFiltersResponseDto,
  JobsResponseDto,
} from "@artha/api-contracts";
import type { JobFiltersState } from "@artha/ui";
import {
  defaultJobFeedParams,
  fetchPublicJobs,
} from "@/modules/common/services/jobApi";
import { normalizeNicheValue } from "@artha/utils";
import { setLastSeenPulseVariant, usePulseVariant } from "@/lib/analytics";
import { useJobFiltersState } from "./useJobFiltersState";

const JOBS_PER_PAGE = 10;
const PUBLIC_PROFILE_DEDUP_BY = "content_hash";

type UsePublicProfileJobsParams = {
  username: string;
  creator: CreatorProfileForMyLink;
  filters?: JobFiltersResponseDto | null;
  personalization?: {
    isSessionLoading?: boolean;
    externalId: string | null;
    isPersonalized: boolean;
    candidatePreferences: CandidatePreferenceSeedDto | null;
    jobsRefreshVersion: number;
  };
};

export const usePublicProfileJobs = ({
  username,
  creator,
  filters,
  personalization,
}: UsePublicProfileJobsParams) => {
  const [currentPage, setCurrentPage] = useState(1);
  const posthog = usePostHog();
  const visitorId = useMemo(
    () =>
      personalization?.externalId ?? posthog?.get_distinct_id?.() ?? undefined,
    [personalization?.externalId, posthog],
  );
  const { variant: pulseVariant, isLoaded: isPulseVariantLoaded } = usePulseVariant();
  const combinedNicheFallback = useMemo(() => {
    const preferenceNicheCategories = creator.settings?.nicheCategories ?? [];
    return Array.from(
      new Set(
        [...(creator.nicheTags ?? []), ...preferenceNicheCategories]
          .map((tag) => tag.trim())
          .filter(Boolean)
          .map(normalizeNicheValue),
      ),
    );
  }, [creator.nicheTags, creator.settings?.nicheCategories]);

  const {
    appliedFilters,
    setAppliedFilters,
    draftFilters,
    setDraftFilters,
    isFiltersOpen,
    setIsFiltersOpen,
    filtersForSheet,
    handleFiltersReset,
    requestParams,
    requestParamsKey,
    preferenceFilterState,
  } = useJobFiltersState({
    creator,
    filters,
    nicheFallback: combinedNicheFallback,
    isPublicProfile: true, // Enable public profile parameter building
  });

  const finalRequestParams = useMemo(
    () => ({
      ...defaultJobFeedParams,
      limit: JOBS_PER_PAGE,
      ...requestParams,
      dedup_by: PUBLIC_PROFILE_DEDUP_BY,
    }),
    [requestParams],
  );
  const requestParamsForApi = useMemo(() => {
    const { categories, ...rest } = finalRequestParams;
    const categoriesValue = Array.isArray(categories)
      ? categories.join(",")
      : categories;
    return {
      ...rest,
      ...(categoriesValue ? { categories: categoriesValue } : {}),
    };
  }, [finalRequestParams]);

  useEffect(() => {
    const timer = window.setTimeout(() => setCurrentPage(1), 0);
    return () => window.clearTimeout(timer);
  }, [appliedFilters]);

  const handleReset = (next?: JobFiltersState) => {
    handleFiltersReset(next);
    setCurrentPage(1);
  };

  const { data, isLoading: isQueryLoading, isError, error, isFetching } = useQuery<
    JobsResponseDto,
    Error
  >({
    queryKey: [
      "publicJobs",
      username,
      requestParamsKey,
      currentPage,
      visitorId,
      pulseVariant,
      personalization?.externalId,
      personalization?.isPersonalized,
      personalization?.jobsRefreshVersion,
    ],
    queryFn: async () => {
      return fetchPublicJobs(
        username,
        {
          ...requestParamsForApi,
          offset: (currentPage - 1) * JOBS_PER_PAGE,
        },
        {
          baseUrl: "/api/job-api",
          creator,
          geoBoost: !appliedFilters.country,
          visitorId,
          candidatePreferences:
            personalization?.candidatePreferences ?? undefined,
          pulseVariant: personalization?.isPersonalized
            ? "pulse"
            : pulseVariant,
        },
      );
    },
    refetchOnWindowFocus: false,
    enabled: !personalization?.isSessionLoading && isPulseVariantLoaded,
  });

  const jobs = useMemo(() => data?.items ?? [], [data]);

  // Remember the retrieval variant so subsequent AUD_JOB_* events carry
  // pulse_variant for the PostHog experiment funnel.
  useEffect(() => {
    if (!data || isFetching) return;
    setLastSeenPulseVariant(data.metadata?.variant);
  }, [data, isFetching]);

  // Derive total from recommend API response (no separate count API needed)
  const totalJobs = data?.total ?? 0;
  const totalPages = totalJobs ? Math.ceil(totalJobs / JOBS_PER_PAGE) : 0;

  return {
    jobs,
    totalJobs,
    totalPages,
    isLoading: isQueryLoading || !!personalization?.isSessionLoading || !isPulseVariantLoaded,
    isFetching,
    isError,
    error,
    currentPage,
    setCurrentPage,
    isFiltersOpen,
    setIsFiltersOpen,
    appliedFilters,
    setAppliedFilters,
    draftFilters,
    setDraftFilters,
    filtersForSheet,
    handleFiltersReset: handleReset,
    preferenceFilterState,
  };
};
